import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import { getCachedData, setCachedData } from './cache';

// Get environment variables with fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Log the values for debugging
console.log('Supabase credentials check:', {
  url: supabaseUrl ? 'Present' : 'Missing',
  key: supabaseKey ? 'Present' : 'Missing'
});

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase credentials:', {
    url: supabaseUrl ? 'Present' : 'Missing',
    key: supabaseKey ? 'Present' : 'Missing'
  });
  throw new Error('Missing Supabase credentials. Please check your .env file.');
}

// Create Supabase client with enhanced configuration
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    global: {
      headers: {
        'x-application-name': 'one80labs-site'
      }
    },
    db: {
      schema: 'public'
    },
    // Add custom fetch options
    fetchOptions: {
      // Increase timeout
      timeout: 60000, // Increased to 60 seconds
      credentials: 'include' // Add credentials for CORS
    }
  }
);

// Enhanced query function with retry logic and timeout
export const enhancedQuery = async (
  queryFn: (signal: AbortSignal) => Promise<any>, 
  options: { 
    timeout?: number;
    retries?: number;
    retryDelay?: number;
  } = {}
) => {
  const {
    timeout = 60000, // Increased to 60 seconds
    retries = 5, // Increased retry attempts
    retryDelay = 2000 // Increased delay between retries
  } = options;

  let attempt = 0;

  while (attempt < retries) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort('timeout'), timeout);

    try {
      const result = await queryFn(controller.signal);
      clearTimeout(timeoutId);
      return result;
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      // Don't retry if aborted or component unmounted
      if (error.message?.includes('unmount') || error.message?.includes('aborted')) {
        throw error;
      }

      attempt++;
      console.warn(`Query attempt ${attempt} failed:`, error);
      
      if (attempt === retries) {
        if (error.message?.includes('fetch')) {
          throw new Error('Unable to connect to Supabase. Please check your network connection and try again.');
        }
        throw error;
      }

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt - 1)));
    }
  }
};

// Test Supabase connection with increased timeout and retries
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await enhancedQuery(
      async (signal) => supabase
        .from('surveys')
        .select('id', { count: 'exact' })
        .limit(1)
        .abortSignal(signal),
      {
        timeout: 30000, // 30 second timeout for connection test
        retries: 5, // More retries for initial connection test
        retryDelay: 2000 // Longer delay between retries
      }
    );

    if (error) {
      console.error('Supabase connection error:', error);
      return false;
    }

    console.log('Supabase connection successful');
    return true;
  } catch (error) {
    console.error('Connection test failed:', error);
    return false;
  }
};

// Get first active survey
export const getFirstActiveSurvey = async (): Promise<string | null> => {
  try {
    const { data, error } = await enhancedQuery(
      async (signal) => supabase
        .from('surveys')
        .select('id')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
        .abortSignal(signal)
    );

    if (error) {
      console.error('Error fetching first active survey:', error);
      return null;
    }

    return data?.id || null;
  } catch (error) {
    console.error('Error in getFirstActiveSurvey:', error);
    return null;
  }
};

// Get blog categories
export const getBlogCategories = async () => {
  try {
    const { data, error } = await enhancedQuery(
      async (signal) => supabase
        .from('blog_categories')
        .select('*')
        .order('name')
        .abortSignal(signal)
    );

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching blog categories:', error);
    throw error;
  }
};

// Get blog posts by category
export const getBlogPostsByCategory = async (categorySlug: string, page: number = 1) => {
  const POSTS_PER_PAGE = 10;
  const offset = (page - 1) * POSTS_PER_PAGE;

  try {
    const { data: posts, error: postsError, count } = await enhancedQuery(
      async (signal) => supabase
        .from('blog_posts')
        .select(`
          *,
          blog_authors (*),
          blog_categories (*),
          blog_tag_posts (
            blog_tags (*)
          )
        `, { count: 'exact' })
        .eq('blog_categories.slug', categorySlug)
        .eq('is_published', true)
        .eq('deleted', false)
        .order('published_at', { ascending: false })
        .range(offset, offset + POSTS_PER_PAGE - 1)
        .abortSignal(signal)
    );

    if (postsError) {
      throw postsError;
    }

    return {
      posts: posts || [],
      total: count || 0
    };
  } catch (error) {
    console.error('Error fetching posts by category:', error);
    throw error;
  }
};

// Get blog authors
export const getBlogAuthors = async () => {
  try {
    const { data, error } = await enhancedQuery(
      async (signal) => supabase
        .from('blog_authors')
        .select('*')
        .order('name')
        .abortSignal(signal)
    );

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching blog authors:', error);
    throw error;
  }
};

// Get blog posts by author
export const getBlogPostsByAuthor = async (authorSlug: string, page: number = 1) => {
  const POSTS_PER_PAGE = 10;
  const offset = (page - 1) * POSTS_PER_PAGE;

  try {
    const { data: posts, error: postsError, count } = await enhancedQuery(
      async (signal) => supabase
        .from('blog_posts')
        .select(`
          *,
          blog_authors (*),
          blog_categories (*),
          blog_tag_posts (
            blog_tags (*)
          )
        `, { count: 'exact' })
        .eq('blog_authors.slug', authorSlug)
        .eq('is_published', true)
        .eq('deleted', false)
        .order('published_at', { ascending: false })
        .range(offset, offset + POSTS_PER_PAGE - 1)
        .abortSignal(signal)
    );

    if (postsError) {
      throw postsError;
    }

    return {
      posts: posts || [],
      total: count || 0
    };
  } catch (error) {
    console.error('Error fetching posts by author:', error);
    throw error;
  }
};

// Get single blog post
export const getBlogPost = async (slug: string, preview: boolean = false) => {
  try {
    const query = supabase
      .from('blog_posts')
      .select(`
        *,
        blog_authors (*),
        blog_categories (*),
        blog_tag_posts (
          blog_tags (*)
        )
      `)
      .eq('slug', slug)
      .single();

    // If not in preview mode, only show published posts
    if (!preview) {
      query.eq('is_published', true).eq('deleted', false);
    }

    const { data, error } = await enhancedQuery(
      async (signal) => query.abortSignal(signal)
    );

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Blog post not found');
      }
      throw error;
    }

    if (!data.is_published && !preview) {
      throw new Error('This blog post is not published yet');
    }

    return data;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

// Initialize connection test
testSupabaseConnection()
  .then(isConnected => {
    console.log('Supabase connection:', isConnected ? 'OK' : 'Failed');
  })
  .catch(console.error);